import React, { useState, useEffect, useRef } from 'react';
import { Box, TextField, List, ListItem, ListItemButton, ListItemText, Paper, CircularProgress, Typography } from '@mui/material';
import axios from 'axios';

// Catálogo completo de pórticos ya lo trae/cachea PorticoLayer via
// /api/vehicles/porticos/all — acá se vuelve a pedir una sola vez al montar
// y el filtrado por nombre es local (sin round-trip por cada tecla, a
// diferencia de SearchVehicle que sí pega a la BD en cada búsqueda).
function PorticoSearch({ onPorticoSelect }) {
  const [query, setQuery] = useState('');
  const [porticos, setPorticos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hasFocus, setHasFocus] = useState(false);

  const searchContainerRef = useRef(null);

  useEffect(() => {
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await axios.get(`${process.env.REACT_APP_VALHALLA_URL_BACKEND}/api/vehicles/porticos/all`);
        if (!response.data.success) throw new Error('Error al obtener datos de pórticos');

        const flat = [];
        response.data.data.forEach(concesionariaGroup => {
          if (concesionariaGroup.porticos) {
            concesionariaGroup.porticos.forEach(portico => {
              flat.push({
                ...portico,
                concesionaria: concesionariaGroup.concesionaria,
                tipo_concesionaria: concesionariaGroup.tipo,
                cod_via: concesionariaGroup.cod_via,
                observaciones: concesionariaGroup.observaciones
              });
            });
          }
        });
        setPorticos(flat);
      } catch (err) {
        console.error('Error loading porticos for search:', err);
        setError(err.message || 'Error al cargar pórticos');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    function handleClickOutside(event) {
      if (searchContainerRef.current && !searchContainerRef.current.contains(event.target)) {
        setHasFocus(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const normalize = (s) => (s || '').toString().toLowerCase();
  const results = query.length >= 2
    ? porticos.filter(p => normalize(p.nombre).includes(normalize(query))).slice(0, 30)
    : [];

  const handleSelect = (portico) => {
    onPorticoSelect(portico);
    setHasFocus(false);
    setQuery(portico.nombre || '');
  };

  return (
    <Box ref={searchContainerRef} sx={{ position: 'relative', width: '100%' }}>
      <TextField
        fullWidth
        label="Buscar pórtico"
        variant="outlined"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onFocus={() => setHasFocus(true)}
        placeholder="Ingrese al menos 2 caracteres"
        disabled={loading && !error}
        sx={{ mb: 1 }}
      />

      {hasFocus && (query.length >= 2 || loading || error) && (
        <Paper
          elevation={3}
          sx={{
            position: 'absolute',
            zIndex: 1000,
            width: '100%',
            maxHeight: 300,
            overflow: 'auto',
            mt: 0.5
          }}
        >
          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
              <CircularProgress size={24} />
            </Box>
          )}

          {error && (
            <Box sx={{ p: 2 }}>
              <Typography color="error">{error}</Typography>
            </Box>
          )}

          {!loading && !error && query.length >= 2 && results.length === 0 && (
            <Box sx={{ p: 2 }}>
              <Typography>No se encontraron resultados</Typography>
            </Box>
          )}

          {!loading && results.length > 0 && (
            <List sx={{ py: 0 }}>
              {results.map((portico) => (
                <ListItem key={portico.id} disablePadding divider>
                  <ListItemButton onClick={() => handleSelect(portico)}>
                    <ListItemText
                      primary={portico.nombre}
                      secondary={
                        <>
                          {portico.concesionaria || 'Sin concesionaria'}
                          {portico.referencia ? ` — ${portico.referencia}` : ''}
                        </>
                      }
                    />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          )}
        </Paper>
      )}
    </Box>
  );
}

export default PorticoSearch;
